import { lazy, Suspense, type ReactElement } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { AppLayout } from './shell/app-layout';
import { HomeScreen } from './screens/home/home-screen';
import { RetiredRoute } from './shell/retired-route';
import { CardGridSkeleton } from './shell/skeleton';

// Route-level code splitting (§12 performance budget). Everything a first-time
// visitor is most likely to land on — home, search, a business page, and the
// not-found fallback — stays in the entry bundle so the common path costs no extra
// round trip. The rest load on navigation, which is what keeps the entry chunk from
// carrying the booking stepper, the whole CMS surface and the claim flow to a
// visitor who only wanted to look at a salon.
const BookingStepperScreen = lazy(() => import('./screens/booking-stepper/booking-stepper-screen').then((m) => ({ default: m.BookingStepperScreen })));
const ConfirmationScreen = lazy(() => import('./screens/confirmation/confirmation-screen').then((m) => ({ default: m.ConfirmationScreen })));
const ForBusinessScreen = lazy(() => import('./screens/for-business/for-business-screen').then((m) => ({ default: m.ForBusinessScreen })));
const PricingScreen = lazy(() => import('./screens/pricing/pricing-screen').then((m) => ({ default: m.PricingScreen })));
const PostAnOfferScreen = lazy(() => import('./screens/post-an-offer/post-an-offer-screen').then((m) => ({ default: m.PostAnOfferScreen })));
const GetFeaturedScreen = lazy(() => import('./screens/get-featured/get-featured-screen').then((m) => ({ default: m.GetFeaturedScreen })));
const BlogScreen = lazy(() => import('./screens/blog/blog-screen').then((m) => ({ default: m.BlogScreen })));
const BlogPostScreen = lazy(() => import('./screens/blog-post/blog-post-screen').then((m) => ({ default: m.BlogPostScreen })));
const AppScreen = lazy(() => import('./screens/app/app-screen').then((m) => ({ default: m.AppScreen })));
const OffersScreen = lazy(() => import('./screens/offers/offers-screen').then((m) => ({ default: m.OffersScreen })));
const TrendingScreen = lazy(() => import('./screens/trending/trending-screen').then((m) => ({ default: m.TrendingScreen })));
const AuthOtpScreen = lazy(() => import('./screens/auth-otp/auth-otp-screen').then((m) => ({ default: m.AuthOtpScreen })));
const AboutScreen = lazy(() => import('./screens/about/about-screen').then((m) => ({ default: m.AboutScreen })));
const PageScreen = lazy(() => import('./screens/pages/page-screen').then((m) => ({ default: m.PageScreen })));
const HelpScreen = lazy(() => import('./screens/help/help-screen').then((m) => ({ default: m.HelpScreen })));
const ContactScreen = lazy(() => import('./screens/contact/contact-screen').then((m) => ({ default: m.ContactScreen })));
const StatusScreen = lazy(() => import('./screens/status/status-screen').then((m) => ({ default: m.StatusScreen })));
const VideosScreen = lazy(() => import('./screens/videos/videos-screen').then((m) => ({ default: m.VideosScreen })));
const ReelLinkScreen = lazy(() => import('./screens/reel-link/reel-link-screen').then((m) => ({ default: m.ReelLinkScreen })));

// A lazy route needs a boundary, and the boundary needs to look like the page that
// is arriving — a blank screen reads as a broken navigation.
function lazyRoute(element: ReactElement): ReactElement {
  return <Suspense fallback={<CardGridSkeleton count={3} />}>{element}</Suspense>;
}
import { NotFoundScreen } from './screens/not-found/not-found-screen';
// The two Listings pages (DR-81) carry the design's own stylesheet and dialogs, so they
// load on demand like every other heavy route (DR-70 entry budget).
const LrResultsScreen = lazy(() => import('./screens/listings/lr-results-screen').then((m) => ({ default: m.LrResultsScreen })));
const LrListingScreen = lazy(() => import('./screens/listings/lr-listing-screen').then((m) => ({ default: m.LrListingScreen })));

// One route → one screen folder (CB-D2). AppLayout is the persistent shell
// (app bar + language toggle + footer); screens render into its outlet. The
// slug-driven CMS pages (FR-A11) all reuse a single <PageScreen slug=…/>.
export const router = createBrowserRouter([
  // The central sign-in (DR-35) is a standalone page in the four-module auth design —
  // no app bar, footer or tab bar (C X1 / P A.5 render full-screen), so it sits
  // outside AppLayout.
  { path: '/login', element: lazyRoute(<AuthOtpScreen intent="signin" />) },
  { path: '/signup', element: lazyRoute(<AuthOtpScreen intent="signup" defaultType="customer" />) },
  { path: '/merchant-signup', element: lazyRoute(<AuthOtpScreen intent="signup" defaultType="business" />) },
  {
    element: lazyRoute(<AppLayout />),
    children: [
      { path: '/', element: <HomeScreen /> },
      { path: '/search', element: lazyRoute(<LrResultsScreen />) },
      // Offers destination (FR-C19) and trending discovery (FR-C20) — §7.3 routes 3 and 4.
      { path: '/offer', element: lazyRoute(<OffersScreen />) },
      { path: '/trending', element: lazyRoute(<TrendingScreen />) },
      // The Videos page (FR-C30): full reviews in a theater and the reel rails.
      { path: '/videos', element: lazyRoute(<VideosScreen />) },
      { path: '/business/:slug', element: lazyRoute(<LrListingScreen />) },
      { path: '/business/:slug/book', element: lazyRoute(<BookingStepperScreen />) },
      // Canonical short deep link (API-49/FR-C14) — the QR/share target
      // /b/{slug}?src=qr[&l={item}] resolves to the same business page.
      { path: '/b/:slug', element: lazyRoute(<LrListingScreen />) },
      // §7.3.1 route 7 — the booking flow under the canonical short link (DC54).
      { path: '/b/:slug/book', element: lazyRoute(<BookingStepperScreen />) },
      // Directory listing (§7.3 route 6, FR-B17) — a seeded business not yet operating
      // on the platform. Redirects to /b/{slug} once its claim is approved.
      { path: '/l/:slug', element: lazyRoute(<LrListingScreen />) },
      // The reel share link (FR-C29 v2.10): the viewer on that reel; closing lands on the listing.
      { path: '/r/:id', element: lazyRoute(<ReelLinkScreen />) },
      { path: '/booking/:code', element: lazyRoute(<ConfirmationScreen />) },
      { path: '/for-business', element: lazyRoute(<ForBusinessScreen />) },
      { path: '/pricing', element: lazyRoute(<PricingScreen />) },
      { path: '/post-an-offer', element: lazyRoute(<PostAnOfferScreen />) },
      { path: '/get-featured', element: lazyRoute(<GetFeaturedScreen />) },
      { path: '/blog', element: lazyRoute(<BlogScreen />) },
      { path: '/blog/:slug', element: lazyRoute(<BlogPostScreen />) },
      // DR-40: /apps is retired — a permanent redirect to /app.
      { path: '/apps', element: <RetiredRoute to="/app" /> },
      // §7.3 route 11 — the address printed on QR posters (FR-C22).
      { path: '/app', element: lazyRoute(<AppScreen />) },

      // DR-23: the per-category route and the merchants content pages are dropped.
      // They keep resolving so existing inbound links and printed material do not
      // break — categories now land on search with the filter pre-applied, and
      // merchant-facing content consolidates into /for-business (§7.3 route 9).
      { path: '/c/:categoryId', element: <RetiredRoute to="/search" carryParam="categoryId" as="category" /> },
      { path: '/merchants', element: <RetiredRoute to="/for-business" /> },
      { path: '/merchant-support', element: <RetiredRoute to="/help" /> },
      { path: '/about', element: lazyRoute(<AboutScreen />) },
      { path: '/help', element: lazyRoute(<HelpScreen />) },
      { path: '/contact', element: lazyRoute(<ContactScreen />) },
      { path: '/status', element: lazyRoute(<StatusScreen />) },
      { path: '/terms', element: lazyRoute(<PageScreen slug="terms" />) },
      { path: '/privacy', element: lazyRoute(<PageScreen slug="privacy" />) },
      { path: '*', element: <NotFoundScreen /> },
    ],
  },
]);
